import React, { useState } from "react";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "../../common/form-input/form-input.component.css";
import { ReactComponent as VisibilityIcon } from "../../../assets/visibility-icon.svg";
import { ReactComponent as VisibilityOffIcon } from "../../../assets/visibility-off-icon.svg";
import { validateEmail } from "../../../utils/validate-email.util";
import { useLoginForm } from "./useLoginForm";
import { ForgotPasswordFormValues, LoginFormValues } from "./LoginForm.types";
import { useStyles } from "./LoginForm.styles";

interface Props {
  handleClose: () => void;
  route?: string;
}

const LoginForm: React.FC<Props> = ({ handleClose, route = "/catalog" }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState(false);

  const {
    forgotMode,
    emailSent,
    onSubmit,
    onForgotSubmit,
    enterForgotMode,
  } = useLoginForm(handleClose, route);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<LoginFormValues>({ mode: "onChange" });

  const {
    register: registerForgot,
    handleSubmit: handleForgotSubmit,
    watch: watchForgot,
    formState: {
      isValid: isForgotValid,
      isSubmitting: isForgotSubmitting,
    },
  } = useForm<ForgotPasswordFormValues>({ mode: "onChange" });

  const emailValue = watch("email");
  const passwordValue = watch("password");
  const forgotEmailValue = watchForgot("email");

  if (forgotMode) {
    if (emailSent) {
      return (
        <div className={classes.emailSentMessage}>
          {t("auth.requestedEmailSent")}
        </div>
      );
    }

    return (
      <form
        onSubmit={handleForgotSubmit(onForgotSubmit)}
        className={classes.form}
      >
        <fieldset disabled={isForgotSubmitting} className={classes.fieldset}>
          <div className="group">
            <input
              id="forgot-email"
              type="text"
              className={clsx("form-input", "h6-regular", classes.fieldFont, classes.fieldInputText)}
              {...registerForgot("email", {
                required: true,
                validate: (value) => validateEmail(value),
              })}
            />
            <label
              htmlFor="forgot-email"
              className={clsx("h6-regular", "form-input-label", classes.fieldFont, {
                shrink: !!forgotEmailValue,
              })}
            >
              {t("auth.Email")}
            </label>
          </div>

          <div className={classes.spacer} />

          <button
            type="submit"
            className={classes.button}
            disabled={isForgotSubmitting || !isForgotValid}
          >
            {isForgotSubmitting
              ? t("auth.sendingResetLink")
              : t("auth.resetPassword")}
          </button>
        </fieldset>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <fieldset disabled={isSubmitting} className={classes.fieldset}>
        <div className="group">
          <input
            id="login-email"
            type="text"
            className={clsx("form-input", "h6-regular", classes.fieldFont, classes.fieldInputText)}
            {...register("email", {
              required: true,
              validate: (value) => validateEmail(value),
            })}
          />
          <label
            htmlFor="login-email"
            className={clsx("h6-regular", "form-input-label", classes.fieldFont, {
              shrink: !!emailValue,
            })}
          >
            {t("auth.Email")}
          </label>
        </div>

        <div className={clsx("group", classes.passwordGroup)}>
          <input
            id="login-password"
            type={showPassword ? "text" : "password"}
            className={clsx("form-input", "h6-regular", classes.fieldFont, classes.fieldInputText)}
            {...register("password", { required: true })}
          />
          <label
            htmlFor="login-password"
            className={clsx("h6-regular", "form-input-label", classes.fieldFont, {
              shrink: !!passwordValue,
            })}
          >
            {t("auth.Password")}
          </label>
          <span
            className={clsx(classes.passwordToggle, {
              [classes.disabledLink]: isSubmitting,
            })}
            onClick={() => {
              if (isSubmitting) return;
              setShowPassword((prev) => !prev);
            }}
          >
            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </span>
        </div>

        <div className={classes.forgotRow}>
          <span
            className={clsx(classes.forgotLink, {
              [classes.disabledLink]: isSubmitting,
            })}
            onClick={() => {
              if (isSubmitting) return;
              enterForgotMode();
            }}
          >
            {t("auth.forgot")}
          </span>
        </div>

        <div className={classes.spacer} />

        <button
          type="submit"
          className={classes.button}
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? t("auth.signingIn") : t("auth.sign in")}
        </button>
      </fieldset>
    </form>
  );
};

export default LoginForm;
